import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import keycloak from './keycloak';
import { setTokenGetter } from './api';

const AuthContext = createContext(null);

// Pages that must render for someone who has never signed in. Everything else
// still requires a session — see the redirect in AppShell.
//
// These used to be unreachable while signed out despite a comment claiming
// otherwise: init ran with onLoad:'login-required', which bounces the browser to
// Keycloak before React renders anything, so /terms and /privacy sent visitors
// to a login form. `check-sso` instead reports whether a session already exists
// and returns either way, leaving the decision to the app.
const PUBLIC_PATHS = ['/', '/terms', '/privacy'];
const isPublicPath = (p) => PUBLIC_PATHS.includes(p.replace(/\/+$/, '') || '/');

// Two-step resume markers used by register() below to get a trustworthy answer
// to "does this browser actually hold a Keycloak session?" before ever hitting
// the registration form. `keycloak.authenticated` can't answer that on its own:
// it only reflects what check-sso's hidden iframe saw, and browsers that block
// third-party cookies in iframes (the current default almost everywhere) make
// that iframe blind to a real, valid session cookie — so it can read false right
// before Keycloak's own submit-time check (a top-level request, not subject to
// the same blocking) finds that same cookie and rejects registration with an
// unrecoverable "already authenticated as different user" page.
//
// VERIFY does a real top-level redirect with prompt=none — not an iframe, so
// third-party cookie blocking doesn't apply — which comes back authenticated
// only if a session genuinely exists, and in that case leaves a real idToken on
// the client. REGISTER resumes into the actual registration form once we know
// (genuinely, either way) that no session remains.
const RESUME_REGISTER_PARAM = 'authAction';
const RESUME_REGISTER_VERIFY = 'verify-register';
const RESUME_REGISTER_VALUE = 'register';

// setTokenGetter's callback runs once per outstanding API call, and a single page
// can easily have several in flight at once. When the refresh token itself has
// expired, each of those calls independently fails updateToken() and would each
// call keycloak.login() — several overlapping full-page redirects, each logging
// its own failed token-endpoint request. This guard collapses that fan-out into
// a single redirect.
let redirectingToLogin = false;
const forceRelogin = () => {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  keycloak.login();
};

// Coarse categories the UI can act on (U5) — distinct from the raw message, which is
// still logged to the console for debugging but not necessarily fit for end users.
// 'session'  — token/auth rejected by the backend; signing in again is the fix.
// 'server'   — reached the backend but it errored (5xx) or returned a bad response.
// 'network'  — never reached the backend at all (offline, DNS, CORS, etc.).
export function AuthProvider({ children }) {
  const [appUser, setAppUser] = useState(null);
  const [ready, setReady]     = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const onPublicPath = isPublicPath(window.location.pathname);

    keycloak.init({
      // check-sso on public pages so a visitor can read them; login-required
      // everywhere else preserves the previous behaviour exactly.
      onLoad: onPublicPath ? 'check-sso' : 'login-required',
      pkceMethod: 'S256',
      checkLoginIframe: false,
    }).then(async (authenticated) => {
      const params = new URLSearchParams(window.location.search);
      const authAction = params.get(RESUME_REGISTER_PARAM);
      const stripAuthAction = () => {
        params.delete(RESUME_REGISTER_PARAM);
        const rest = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
      };

      if (authenticated && authAction === RESUME_REGISTER_VERIFY) {
        // register()'s verification round-trip came back authenticated: a real
        // session exists and we now have a genuine idToken, so this logout call
        // actually includes id_token_hint and really terminates it (unlike a
        // logout attempted without one). Resume registration once that's done —
        // don't fall through to rendering the app as this user.
        stripAuthAction();
        keycloak.logout({
          redirectUri: `${window.location.origin}/?${RESUME_REGISTER_PARAM}=${RESUME_REGISTER_VALUE}`,
        });
        return;
      }

      if (!authenticated) {
        // On a public page this is the normal case, not a failure: render it
        // signed out. Anywhere else, send them to sign in as before.
        if (onPublicPath) {
          // Reached genuinely unauthenticated either way: straight off
          // register()'s verify round-trip (no session existed at all) or
          // after the logout it triggered above (one did, and is now gone).
          if (authAction === RESUME_REGISTER_VERIFY || authAction === RESUME_REGISTER_VALUE) {
            stripAuthAction();
            keycloak.register({ redirectUri: `${window.location.origin}/` });
            return;
          }
          setReady(true);
          return;
        }
        keycloak.login();
        return;
      }

      setTokenGetter(async () => {
        try { await keycloak.updateToken(60); } catch { forceRelogin(); }
        return keycloak.token;
      });

      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });
        if (res.ok) {
          setAppUser(await res.json());
        } else {
          const body = await res.text().catch(() => '');
          console.error('[auth] /api/auth/me failed', res.status, body);
          setAuthError({
            kind: (res.status === 401 || res.status === 403) ? 'session' : 'server',
            detail: `${res.status}${body ? `: ${body}` : ''}`,
          });
        }
      } catch (err) {
        console.error('[auth] /api/auth/me threw', err);
        setAuthError({ kind: 'network', detail: err.message });
      } finally {
        setReady(true);
      }
    }).catch((err) => {
      console.error('[auth] keycloak.init() rejected:', JSON.stringify(err, Object.getOwnPropertyNames(err || {})), err);
      // A public page has nothing to show an auth error for, and blanking the
      // landing page because the identity server is unreachable would be a
      // worse outage than the one that caused it.
      if (!onPublicPath) setAuthError({ kind: 'session', detail: err?.error || 'keycloak init failed' });
      setReady(true);
    });

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(60).catch(forceRelogin);
    };
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  // Both land the user back at the app root once Keycloak is done with them,
  // where AppShell routes them on to the right place — /setup for a brand-new
  // account, the deals list for an existing one.
  const login = useCallback(() => {
    keycloak.login({ redirectUri: `${window.location.origin}/` });
  }, []);

  // Keycloak's hosted registration form. registrationAllowed is on in the realm
  // template; if it were ever turned off this falls back to the login screen,
  // which is the correct place to send someone who cannot self-register.
  //
  // If this browser already holds an SSO session cookie for a different (or
  // even the same) user, jumping straight to kc_action=REGISTER makes Keycloak
  // refuse with an unrecoverable "already authenticated as different user" page
  // that has no link back into the app, with no way to recover short of manually
  // clearing cookies. `keycloak.authenticated` can't be trusted to rule this out
  // (see the RESUME_REGISTER_VERIFY comment above), so rather than guess, kick
  // off a real top-level prompt=none round-trip first — the init effect handles
  // both outcomes (real session found vs. genuinely none) and only ever calls
  // keycloak.register() once that's settled one way or the other.
  const register = useCallback(() => {
    keycloak.login({
      prompt: 'none',
      redirectUri: `${window.location.origin}/?${RESUME_REGISTER_PARAM}=${RESUME_REGISTER_VERIFY}`,
    });
  }, []);

  return (
    <AuthContext.Provider value={{ appUser, setAppUser, ready, logout, login, register, authError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
